import {
  AUTH_LOGIN,
  AUTH_LOGIN_SUCCESS,
  AUTH_LOGIN_ERROR,
  AUTH_LOGOUT,
  AUTH_UPDATE_USER,
  AUTH_LOGOUT_RESET,
  AUTH_PASSWORD_CHANGE_FORM_DATA_REQUEST,
  AUTH_PASSWORD_CHANGE_FORM_DATA_SUCCESS,
  AUTH_PASSWORD_CHANGE_FORM_DATA_ERROR,
  AUTH_PASSWORD_CHANGE_SUCCESS,
  AUTH_PASSWORD_CHANGE_ERROR,
  AUTH_PASSWORD_REQUEST_SUCCESS,
  AUTH_PASSWORD_REQUEST_ERROR,
  AUTH_SET_AGENCY_SUCCESS
} from '@constants/action-types';
import { replace } from 'connected-react-router';
import axios from 'axios';
import { getServerErrorAction } from './shared-actions';
import { pushApplicationMessage } from './messages-actions';
import { getAuthRequestUrl, BASE_API_URL } from '@constants/endpoints';
import {
  authSelector,
  clearAuth,
  getDefaultRoute,
  notifyAuthLoginUpdate,
  setAuthHeaders,
  setAuthToken,
  setAuthUsername
} from '@utils/auth-utils';
import { setPermissions, setAgencyPermission } from '@utils/permission-utils';

const authLoginStart = () => {
  notifyAuthLoginUpdate();
  return {type: AUTH_LOGIN};
};

export const authLoginSuccess = () => {
  notifyAuthLoginUpdate();
  return { type: AUTH_LOGIN_SUCCESS };
};

const authLoginError = error => {
  notifyAuthLoginUpdate();
  return {
    type: AUTH_LOGIN_ERROR,
    payload: error
  };
};

export const setAgencySuccess = agencyId => ({
  type: AUTH_SET_AGENCY_SUCCESS,
  agencyId
});

// Authenticate based on a token:
export const authTokenLogin = (location, defaultUrl, token, userId, timestamp) => {
  clearAuth();
  const url = getAuthRequestUrl('api-temp-token-auth');
  // eslint-disable-next-line id-length
  const request = axios.post(url, { ts: timestamp, tk: token, u: userId});
  return dispatch => {
    dispatch(authLoginStart());
    request.then(
      data => {
        setAuthToken(data.data.token);
        dispatch(authLoginSuccess());
        setPermissions({...data.data});
        setAuthHeaders();
        setAuthUsername(data.data.username);
        dispatch(setAgencySuccess(data.data.agency_id));
        dispatch(replace(getDefaultRoute(location, defaultUrl)));
      },
      error => {
        clearAuth();
        dispatch(authLoginError(error));
        // If auth fails, navigate to the destination page anyways
        // The router will overwrite the url with the standard login page when auth is invalid
        dispatch(replace(getDefaultRoute(location, defaultUrl)));
      }
    );
  };
};

export const authLogin = (user, location, defaultUrl) => {
  // Ensure auth information is clear before performing an auth action.
  clearAuth();
  const url = getAuthRequestUrl('api-token-auth');
  const request = axios.post(url, { username: user.username, password: user.password});
  return dispatch => {
    dispatch(authLoginStart());
    request.then(
      data => {
        setAuthToken(data.data.token);
        dispatch(authLoginSuccess());
        setPermissions({...data.data});
        setAuthHeaders();
        setAuthUsername(data.data.username);
        dispatch(setAgencySuccess(data.data.agency_id));
        dispatch(replace(getDefaultRoute(location, defaultUrl)));
      },
      error => {
        clearAuth();
        dispatch(authLoginError(error));
        // If auth fails stay on the same page no need to navigate.
      }
    );
  };
};

const authLoginCleared = () => {
  notifyAuthLoginUpdate();
  return { type: AUTH_LOGOUT };
};
const baseLogoutReset = () => ({ type: AUTH_LOGOUT_RESET });

export const authClear = () => dispatch => {
  clearAuth();
  dispatch(authLoginCleared());
};

export const authLogout = () => dispatch => {
  // Clears auth and resets state to remove user data
  dispatch(authClear());
  dispatch(baseLogoutReset());
  // Don't do any path management or anything else
  // The router's auth check will automatically overwrite the url with an appropriate login path
};

// Reconciles the auth.valid state with the current auth token validity,
// e.g. after another tab notifies a login state change.
export const syncAuthValid = (location, defaultUrl) => (dispatch, getState) => {
  const { auth } = getState();
  const tokenIsValid = authSelector();
  if (auth.valid && !tokenIsValid) {
    dispatch(authLogout());
  } else if (!auth.valid && tokenIsValid) {
    setAuthHeaders();
    dispatch(authLoginSuccess());
    if (location && defaultUrl) {
      dispatch(replace(getDefaultRoute(location, defaultUrl)));
    }
  }
};

const authUpdateUser = (field, value) => ({
  type: AUTH_UPDATE_USER,
  payload: {[field]: value}
});

export const authSetUserField = (field, value) => dispatch => dispatch(authUpdateUser(field, value));

const savePasswordChangeRequestSuccess = data => ({
  type: AUTH_PASSWORD_REQUEST_SUCCESS,
  payload: { status: data.status }
});

const savePasswordChangeRequestError = error => ({
  type: AUTH_PASSWORD_REQUEST_ERROR,
  error: {
    status: 400,
    data: error
  }
});

export const sendPasswordChangeRequest = email => {
  const url = getAuthRequestUrl('user/password_reset_request', false);
  const request = axios.post(url, { email });
  return dispatch =>
    request.then(
      data => dispatch(savePasswordChangeRequestSuccess(data)),
      error => dispatch(savePasswordChangeRequestError(error))
    );
};

export const getPasswordChangeFormData = (userPk, token) => async dispatch => {
  const url = getAuthRequestUrl(`user/password_reset/${userPk}/${token}`, false);
  dispatch({
    type: AUTH_PASSWORD_CHANGE_FORM_DATA_REQUEST,
    userPk,
    token
  });
  const request = axios.get(url);
  let response;
  try {
    response = await request;
  } catch (error) {
    if (error.response) {
      return dispatch({
        type: AUTH_PASSWORD_CHANGE_FORM_DATA_ERROR,
        error: {
          status: error.response.status,
          data: error.response.data
        }
      });
    }
    return dispatch({
      type: AUTH_PASSWORD_CHANGE_FORM_DATA_ERROR,
      error: {
        status: 400,
        data: {}
      }
    });
  }
  return dispatch({
    type: AUTH_PASSWORD_CHANGE_FORM_DATA_SUCCESS,
    payload: {
      status: response.status,
      data: response.data
    }
  });
};

const savePasswordChangeSuccess = data => ({
  type: AUTH_PASSWORD_CHANGE_SUCCESS,
  payload: { status: data.status }
});

const savePasswordChangeError = error => ({
  type: AUTH_PASSWORD_CHANGE_ERROR,
  error: {
    status: 400,
    data: error
  }
});

export const sendNewPasswords = ({ userPk, token, ...requestData }) => {
  const url = getAuthRequestUrl(`user/password_reset/${userPk}/${token}`, false);
  const request = axios.post(url, requestData);
  return dispatch =>
    request.then(
      data => dispatch(savePasswordChangeSuccess(data)),
      error => dispatch(savePasswordChangeError(error.response.data || {}))
    );
};

export const setAgency = agencyId => {
  const url = `${BASE_API_URL}/agency/user/`;
  const request = axios.patch(url, { agency: agencyId });
  return dispatch =>
    request.then(
      () => {
        setAgencyPermission(agencyId);
        dispatch(setAgencySuccess(agencyId));
        // Reload the page to reload config after setting the agency.
        location.reload();
      }
    );
};

export const resendOnboardingEmail = userId => {
  const url = `${BASE_API_URL}/user/${userId}/resend_onboarding/`;
  const request = axios.get(url);
  return dispatch =>
    request.then(
      () => {
        dispatch(pushApplicationMessage('The onboarding email was resent.'));
      },
      error => dispatch(getServerErrorAction('user', error))
    );
};
